/**
 * Copyright © Veeam Software Group GmbH.
 */

/**
 * Copyright © Veeam Software Group GmbH.
 */

/* eslint-disable @typescript-eslint/naming-convention */
import { generateGuid } from '../../../../helpers/string-helpers';

import type { FileTransportRequestOptions, StreamProgressData } from '../../../../services/file-transport/interfaces';
import type { TransportMiddlewareContext, TransportMiddlewareRequest, TransportMiddlewareResponse, NextFunction } from '../../../../core';

const fileUidHeader = 'X-File-Uid';
const blobSlice = File.prototype.slice || (File.prototype as any).mozSlice || (File.prototype as any).webkitSlice;

export const processStreamUploadBodyMiddleware = <T, R, E>(
    next: NextFunction<TransportMiddlewareResponse<R, E> | Error>,
    ctx: TransportMiddlewareContext,
    req: TransportMiddlewareRequest<T>,
    res: TransportMiddlewareResponse<R, E>
) => {
    if (!req.streamData) {
        const { originalData, options, headers } = req;
        const { streamOptions } = options as FileTransportRequestOptions;
        const files = originalData as unknown as any[];
        const { chunkSize, progress } = streamOptions ?? {};
        const totalFiles = files.length;

        const filesData = files.map(({ blobFile: file, customHeaders }, index) => {
            const fileSize = file.size;
            const fileNumber = index + 1;
            const defaultFileId: string = generateGuid();
            const chunkLength: number = chunkSize && chunkSize > 0 && chunkSize <= fileSize
                ? Math.ceil(fileSize / chunkSize)
                : 1;
            const baseHeaders = {
                ...headers as Record<string, string>,
                [fileUidHeader]: defaultFileId,
                ...customHeaders,
                'X-Content-Name': file.name,
                'Content-Length': `${fileSize}`,
                'X-Content-Length': `${fileSize}`,
            };
            const fileId = baseHeaders[fileUidHeader];

            if (chunkLength > 1) {
                return new Array(chunkLength).fill(0)
                    .map((empty, chunkIndex) => {
                        const fileStart: number = chunkIndex * chunkSize;
                        const fileEnd: number = chunkIndex === chunkLength - 1
                            ? Math.min(fileSize, fileStart + chunkSize)
                            : fileStart + chunkSize;
                        const chunk = blobSlice.call(file, fileStart, fileEnd);

                        return {
                            fileId,
                            chunk,
                            chunkSize: chunk.size,
                            headers: {
                                ...baseHeaders,
                                'Content-Length': `${chunk.size}`,
                                'X-Chunk-Number': `${chunkIndex + 1}`,
                                'X-Total-Chunks': `${chunkLength}`,
                                'X-File-Number': `${fileNumber}`,
                                'X-Total-Files': `${totalFiles}`,
                            },
                        };
                    });
            }

            return {
                fileId,
                file,
                fileSize,
                headers: {
                    ...baseHeaders,
                    'X-Chunk-Number': '1',
                    'X-Total-Chunks': '1',
                    'X-File-Number': `${fileNumber}`,
                    'X-Total-Files': `${totalFiles}`,
                },
            };
        });

        req.streamData = {
            uploadFilesData: filesData,
            progress,
            uploadedFilesSize: 0,
            fullSize: files.reduce((res, file) => res + file.blobFile.size, 0),
            currentPieceSendAttempts: 1,

            fileNumber: 1,
            totalFiles,
            chunkProgress: {},
        } as StreamProgressData;
    }

    next();
};
